import type {ReactNode} from 'react';

import styled from '@emotion/styled';
import {spacing} from '@nfq/react-grid';

import {useMapCategoryFilter} from './useMapCategoryFilter';

/** A single category option shown in the filter bar. */
export interface MapCategoryFilterOption {
    /** Optional icon rendered before the label. */
    icon?: ReactNode;
    /** Unique category identifier. */
    id: string;
    /** Display name of the category. */
    name: string;
}

/**
 * The props for the `MapCategoryFilter` component.
 * Configures a floating, horizontally draggable category filter bar overlaid on the map.
 */
interface ComponentProps {
    /** Available category options. */
    categories: MapCategoryFilterOption[];
    /** Called when a category chip is toggled. */
    onToggle(categoryId: string): void;
    /** Currently selected category IDs. Empty means "all categories". */
    selectedCategoryIds: string[];
    /**
     * The `testId` property represents a unique identifier assigned to a component for testing purposes.
     * It is applied as `data-cy` attribute to enable reliable targeting in Cypress tests.
     *
     * @default 'MapCategoryFilter'
     */
    testId?: string;
}

/**
 * Renders a floating, horizontally draggable row of category filter chips overlaid on the map.
 * Built on keen-slider (like the walk slider at the bottom of the map) so it has proper drag
 * momentum on both touch and mouse, instead of relying on native `overflow-x` scrolling.
 * Multi-select — a walk stays visible if it has at least one of the selected categories.
 * An empty selection shows every walk.
 *
 * @param props                     The component props.
 * @param props.categories          Available category options.
 * @param props.onToggle            Called with a category ID when its chip is toggled.
 * @param props.selectedCategoryIds Currently selected category IDs.
 * @param props.testId              An optional test ID used for automation selectors. Defaults to `'MapCategoryFilter'`.
 * @returns A JSX element rendering the MapCategoryFilter.
 *
 * @example
 * ```tsx
 * <MapCategoryFilter categories={categories} selectedCategoryIds={selected} onToggle={toggleCategory} />
 * ```
 */
const MapCategoryFilter = ({
    categories,
    onToggle,
    selectedCategoryIds,
    testId = 'MapCategoryFilter'
}: ComponentProps) => {
    const {sliderRef, t} = useMapCategoryFilter();

    if (categories.length === 0) return null;

    return (
        <Wrapper data-cy={testId}>
            <Track ref={sliderRef} className="keen-slider" data-cy={`${testId}-track`}>
                <Slide className="keen-slider__slide">
                    <Chip
                        $isActive={selectedCategoryIds.length === 0}
                        data-cy={`${testId}-all`}
                        type="button"
                        onClick={() => selectedCategoryIds.forEach(onToggle)}
                    >
                        {t('karte.allCategories')}
                    </Chip>
                </Slide>
                {categories.map(category => (
                    <Slide key={category.id} className="keen-slider__slide">
                        <Chip
                            $isActive={selectedCategoryIds.includes(category.id)}
                            data-cy={`${testId}-chip-${category.id}`}
                            type="button"
                            onClick={() => onToggle(category.id)}
                        >
                            {category.icon}
                            {category.name}
                        </Chip>
                    </Slide>
                ))}
            </Track>
        </Wrapper>
    );
};

MapCategoryFilter.displayName = 'MapCategoryFilter';

export {MapCategoryFilter};

// ── Styled ──────────────────────────────────────────────────

/*
 * \`left\`/\`right\` clear Leaflet's own zoom control (top-left) and the fullscreen/locate
 * buttons (top-right) at rest, but the keen-slider track isn't clipped to \`Wrapper\`'s bounds
 * (no \`overflow: hidden\`, matching the walk slider's own track) — while dragging, chips can
 * slide visually past those edges. \`z-index\` is kept below the buttons (900) and Leaflet's
 * control pane (\`.leaflet-top\`, 1000) so an overflowing chip renders behind them, never
 * covering a button, rather than on top of it.
 */
const Wrapper = styled.div`
    left: 5.6rem;
    padding: ${spacing(1)} 0;
    position: absolute;
    right: 6.4rem;
    top: ${spacing(4)};
    z-index: 899;
`;

const Track = styled.div`
    -webkit-tap-highlight-color: transparent;
    -webkit-touch-callout: none;
    display: flex;
    touch-action: pan-y;
    user-select: none;
`;

const Slide = styled.div`
    flex-shrink: 0;
`;

interface ChipProps {
    /** Whether this chip is currently selected. */
    $isActive: boolean;
}

const Chip = styled.button<ChipProps>`
    align-items: center;
    backdrop-filter: blur(8px);
    background: ${({$isActive, theme}) => ($isActive ? theme.colors.primaryDefault : 'rgba(255, 255, 255, 0.85)')};
    border: none;
    border-radius: 9999px;
    color: ${({$isActive, theme}) => ($isActive ? theme.colors.white : theme.colors.inkSecondary)};
    cursor: pointer;
    display: flex;
    font-family: ${({theme}) => theme.fonts.PlusJakartaSans};
    font-size: 1.3rem;
    font-weight: 600;
    gap: ${spacing(1.5)};
    padding: ${spacing(2)} ${spacing(4)};
    white-space: nowrap;
`;